import { createContext, useContext, useMemo } from 'react';
import { useProgress } from '../hooks/useProgress.js';
import { useAuthContext } from './AuthContext.js';

// Create the context object.
// We initialize it with `null` as the default value.
const ProgressContext = createContext(null);

// This is the custom hook for components to easily access the context.
// It checks if the hook is being used within the provider to prevent errors.
export const useProgressContext = () => {
    const context = useContext(ProgressContext);
    if (!context) {
        throw new Error('useProgressContext must be used within a ProgressProvider');
    }
    return context;
};

// This is the provider component that will wrap a part of your application.
// It runs the `useProgress` hook and provides its return value to the context.
export const ProgressProvider = ({ children }) => {
    const { user } = useAuthContext();
    const progressData = useProgress(user);

    // We use `useMemo` to ensure the context value only changes when
    // `progressData` itself changes, preventing unnecessary re-renders.
    const contextValue = useMemo(() => progressData, [progressData]);

    return (
        <ProgressContext.Provider value={contextValue}>
            {children}
        </ProgressContext.Provider>
    );
};
